import { FC, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useLocation } from "react-router-dom"

import './AssetInvoicesByRequest.scss'

import IconEye from "assets/img/icon-eye.svg?react"
import Text from "components/Text/Text"
import Button from "components/Button/Button"
import AssetInvoiceOutgoingByRequest from "models/AssetInvoiceOutgoing"
import { Flex } from "components/Flex"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import reservationSidebars from "stores/reservationSidebars"
import AdHocInvoiceIcon from "components/AdHocInvoiceIcon/AdHocInvoiceIcon"
import { Grid, GridItem } from "components/Grid"
import NoValue from "components/NoValue/NoValue"
import Spoiler from "components/Spoiler/Spoiler"
import classNames from 'classnames'
import { Guid } from "@/types"
import Routes from "routes"

interface IAssetInvoicesBYRequestProps {
    data: AssetInvoiceOutgoingByRequest
    assetId: Guid
    isSale?: boolean
    expanded?: boolean
    toggleExpanded?: (value: boolean, id: Guid) => void
}


const AssetInvoicesByRequest: FC<IAssetInvoicesBYRequestProps> = ({
    data,
    assetId,
    isSale,
    expanded,
    toggleExpanded
}) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()

    const openReservationSidebar = useCallback((reservationId: string,) => {
        reservationSidebars.show({
            reservationId,
        })
    }, [])

    const renderHeader = (toggle: () => void) => (
        <Flex justifyContent="space-between" onClick={toggle} className={classNames(!isSale && "asset-invoices-by-request__header", expanded && "expanded")}>
            <Flex flow="column" alignItems="flex-start">
                <FlexibleContent className="reservation-button" text={data.requestNumber} onClick={() => openReservationSidebar(data.requestId)} />
                <Text className="color-grey-500" type="pSmallest">{data.customerName}</Text>
            </Flex>
            <Flex flow="column">
                <Text bold className="text-right">{data.totalAmount.print()}</Text>
                {data.rentalFeeAmount && <Text type="pSmallest" className="color-grey-500 text-right">{t("invoices.rentalFees")}: {data.rentalFeeAmount.print()}</Text>}
            </Flex>
        </Flex>
    )


    return (
        <Flex flow="column" className={classNames("round-sm  p-3  mb-2", isSale ? "bg-grey-50" : "pl-7 border-grey-200")}>

            <Spoiler
                id={data.requestId}
                expanded={expanded || isSale}
                onToggle={toggleExpanded ?? (() => { })}
                toggleEl={(_, toggle) => renderHeader(toggle)}>
                <Grid columns="40% 22% 30% auto" className="border-grey-100-between py-2">
                    <Text bold type="pSmallest" className="color-grey-500">{t('invoices.invoiceDate')}</Text>
                    <Text bold type="pSmallest" className="color-grey-500">{t('outgoingInvoices.orderNo')}</Text>
                    <Text bold type="pSmallest" className="text-right color-grey-500">{t('asset.assignedAmount')}</Text>
                    <GridItem />
                </Grid>
                {
                    data.entities.map(invoice => (
                        <Grid key={invoice.id} columns="40% 22% 30% auto" className="border-grey-100-between  py-2">
                            <GridItem>
                                <Flex alignItems='center' gap="1rem">
                                    {invoice.from.format(t('config.dateFormat'))}{invoice.to && ` - ${invoice.to.format(t('config.dateFormat'))}`}
                                    {invoice.isAdHoc && <AdHocInvoiceIcon />}
                                </Flex>
                                {!invoice.isAdHoc && !!invoice.daysInUse &&
                                    <Text type="pSmallest" className="color-grey-500">{t("asset.xDaysInUse", { count: invoice.daysInUse })}</Text>
                                }

                            </GridItem>
                            <GridItem>
                                <Text>{invoice.orderNumber || <NoValue />}</Text>
                            </GridItem>
                            <GridItem alignSelf="flex-end">
                                <Text className="text-right" bold >{invoice.assignedAmount.print()}</Text>
                                {invoice.rentalFeeAmount &&
                                    <Text
                                        type="pSmallest"
                                        className="text-right color-grey-500">
                                        {t("invoices.rentalFees")}: {invoice.rentalFeeAmount.print()}
                                    </Text>}
                            </GridItem>
                            <GridItem alignSelf="center" className="pl-2">
                                <Button
                                    round
                                    theme="grey"
                                    ariaLabel={t('outgoingInvoiceControls.viewInvoice')}
                                    icon={<IconEye />}
                                    onClick={() => navigate(Routes.outgoingInvoiceView.getPath({ invoiceId: invoice.id }), { state: { backUrl: location.pathname + location.search, reopenAssetSidebarId: assetId } })}
                                />
                            </GridItem>
                        </Grid>
                    ))
                }
            </Spoiler>
        </Flex>
    )
}

export default AssetInvoicesByRequest
